import React from "react";
import { Maximize2Icon as MaximizeIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as S from "./styled";
import { Label } from "../Typography";
import Modal, { ModalCloseButton } from "../Modal";
import Flex from "../Flex";
import { SPACE } from "../../constants";

export type TPicturesProps<T> = React.ComponentProps<typeof S.Pictures> & {
    header?: string;
    limit?: number;
    items: T[];
    closeButtonTitle: string;
    renderMoreLabel?: (count: number) => React.ReactNode;
    getKey?: (item: T, index: number) => React.Key;
    getImageSrc: (item: T, index: number) => string;
    getImageTitle: (item: T, index: number) => string;
};

const defaultRenderMoreLabel: TPicturesProps<unknown>["renderMoreLabel"] = count => {
    if (count < 0) {
        return "View less";
    }

    return (
        <span>
            View
            {SPACE}
            <Label size="label-small-semibold" color="text-primary-900" styles={{ display: "inline" }}>
                {count}
            </Label>
            {SPACE}
            more
        </span>
    );
};

const Pictures = <T,>({
    header,
    items,
    limit = Number.POSITIVE_INFINITY,
    getKey = (item, index) => index,
    getImageSrc,
    getImageTitle,
    closeButtonTitle,
    renderMoreLabel = defaultRenderMoreLabel,
    ...props
}: TPicturesProps<T>) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [maximiseIndex, setMaximiseIndex] = React.useState<number | null>(null);
    const [showMore, setShowMore] = React.useState(false);

    const hasMore = items.length > limit;
    const itemsToShow = showMore || !hasMore ? items : items.slice(0, limit - 1);

    return (
        <>
            <S.Pictures {...props}>
                {header && <S.Header>{header}</S.Header>}
                <S.PictureList>
                    {itemsToShow.map((item, index) => {
                        const title = getImageTitle(item, index);

                        return (
                            <S.PicturePreview
                                key={getKey(item, index)}
                                onClick={() => {
                                    setMaximiseIndex(index);
                                    setIsModalOpen(true);
                                }}
                            >
                                <S.PreviewHeader>
                                    <Label size="label-small-medium" color="text-primary-900">
                                        {title}
                                    </Label>
                                    <MaximizeIcon size={16} />
                                </S.PreviewHeader>
                                <S.PictureImage src={getImageSrc(item, index)} alt={getImageTitle(item, index)} />
                            </S.PicturePreview>
                        );
                    })}
                    {hasMore && (
                        <S.MoreToggle onClick={() => setShowMore(v => !v)}>
                            <Flex spacing={1.5} alignItems="center">
                                <Label size="label-small-medium" color="text-primary-900">
                                    {showMore
                                        ? renderMoreLabel(limit - items.length)
                                        : renderMoreLabel(items.length - itemsToShow.length)}
                                </Label>
                                {showMore ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                            </Flex>
                        </S.MoreToggle>
                    )}
                </S.PictureList>
            </S.Pictures>
            {maximiseIndex !== null && (
                <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <Flex column spacing={2}>
                        <S.FullImage
                            src={getImageSrc(items[maximiseIndex], maximiseIndex)}
                            alt={getImageTitle(items[maximiseIndex], maximiseIndex)}
                        />
                        <Label size="label-medium-medium" color="text-primary-900">
                            {getImageTitle(items[maximiseIndex], maximiseIndex)}
                        </Label>
                        <Flex justifyContent="end">
                            <ModalCloseButton title={closeButtonTitle} />
                        </Flex>
                    </Flex>
                </Modal>
            )}
        </>
    );
};

export default Pictures;
